import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import Spinner from 'sushiweb/Spinner';
import HorizontalTabs from 'sushiweb/HorizontalTabs';
import red from 'sushiweb/tokens/color/red';
import Breadcrumb from 'sushiweb/Breadcrumb';
import H5 from 'sushiweb/Typography/Heading/H5';

import Layout from 'sushiweb/Layout/Layout';
import {
  applicableFlowTypes,
  currentFlowTypeEnum,
  paymentRequestStatus,
} from '../common/components/constants';
import {
  employeeCountForAdhocPrograms,
  formatAmountInRupees,
  formatIDForGifting,
  isProgramEnded,
  isValidPaymentRequest,
  statusForAdhocPrograms,
} from '../../helpers';
import {
  BreadCrumbWrapper,
  DashboardWrapper,
  HeadWrapper,
  LayoutContainers,
  SpinnerHolder,
  StyledLayout,
  TabContainer,
  tabStyle,
  TabWrapper,
} from '../common/styles';

import Banner from '../common/components/Banner';
import {
  DASHBOARD_ROUTE,
  HOME_ROUTE,
  GIFTING_PLAN_INFO,
  GIFTING_ROUTE,
} from '../../constants/routes';
import { GC_PLAN } from '../WalletPrograms/CreateProgramModal/ProgramForms/common/Components/constants';
import DetailsWrapper from '../common/components/DetailsWrapper/DetailsWrapper';
import { ActionComponentContainer } from '../Payments/styles';
import PlanDetailsBtns from '../WalletPrograms/PlanDetails';
import GiftingProgramPaymentTable from './GiftingProgramPaymentTable';
import GiftingProgramRecipientsTable from './GiftingProgramRecipientsTable';
import ZTrackingClient from '../../jumbo/ZTrackingClient';

const GiftingProgramInfo = ({
  currentFlow,
  viewOnlyMode,
  programInfo,
  paymentRequestDetails,
  getProgramInfo,
  fetchingProgramInfo,
  selectedWorkspace,
}) => {
  const [activeIndexGifting, setActiveIndexGifting] = useState(0);
  const { programId } = useParams();

  const handleTabsClick = val => () => {
    setActiveIndexGifting(val);
  };

  useEffect(() => {
    ZTrackingClient.recordImpression({
      ui_source: 'GIFTING_DETAILS',
      component: 'TABLE_DATA',
    });
  }, []);

  useEffect(() => {
    if (selectedWorkspace && selectedWorkspace.workspace_id)
      getProgramInfo({
        workspaceId: selectedWorkspace.workspace_id ?? '',
        programId,
      });
  }, [selectedWorkspace, currentFlow]);

  const showSelectedTable = () => {
    if (activeIndexGifting === 0)
      return <GiftingProgramRecipientsTable programDetails={programInfo} />;
    return <GiftingProgramPaymentTable programDetails={programInfo} />;
  };

  const ActionComponent = () => {
    return (
      <ActionComponentContainer>
        <div>
          <PlanDetailsBtns
            plan={programInfo}
            title={GC_PLAN}
            toStep={3}
            btnSize='medium'
          />
        </div>
      </ActionComponentContainer>
    );
  };

  return fetchingProgramInfo ? (
    <DashboardWrapper>
      <SpinnerHolder className='center'>
        <Spinner color={red.z_red} size='medium' />
      </SpinnerHolder>
    </DashboardWrapper>
  ) : (
    <DashboardWrapper>
      {viewOnlyMode && <Banner />}
      <BreadCrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-400, #AFB4C0)' }}
            linkTo={`${DASHBOARD_ROUTE}#${HOME_ROUTE}`}
          >
            Home
          </Breadcrumb.Link>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-800, #AFB4C0)' }}
            linkTo={`${DASHBOARD_ROUTE}#${GIFTING_ROUTE}`}
          >
            Gifting
          </Breadcrumb.Link>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-800, #414A5D)' }}
            linkTo={GIFTING_PLAN_INFO}
          >
            {formatIDForGifting(programInfo?.program_id)}
          </Breadcrumb.Link>
        </Breadcrumb>
      </BreadCrumbWrapper>
      <HeadWrapper style={{ marginTop: '20px' }}>
        <HeadWrapper.Left>
          <H5>
            {' '}
            {formatIDForGifting(programInfo.program_id)}
            {' '}
          </H5>
          &nbsp; &nbsp; &nbsp;
          {statusForAdhocPrograms(programInfo)}
        </HeadWrapper.Left>
        {isValidPaymentRequest(paymentRequestDetails) &&
          !isProgramEnded(programInfo) &&
          paymentRequestDetails?.status ===
          paymentRequestStatus.ACTIVE_PENDING && <ActionComponent />}
      </HeadWrapper>
      <LayoutContainers>
        <StyledLayout gap={20}>
          <Layout.Item colD={3} colT={3} colM={3}>
            <DetailsWrapper
              label='Occasion'
              value={programInfo?.gift_card_details?.type_name}
            />
          </Layout.Item>
          <Layout.Item colD={3} colT={3} colM={3}>
            <DetailsWrapper
              label='No. of gift card'
              value={employeeCountForAdhocPrograms(programInfo)}
            />
          </Layout.Item>
          <Layout.Item colD={3} colT={3} colM={3}>
            <DetailsWrapper
              label='Amount Per Gift Card'
              value={formatAmountInRupees(
                programInfo?.gift_card_details?.amount,
              )}
            />
          </Layout.Item>
          <Layout.Item colD={3} colT={3} colM={3}>
            <DetailsWrapper
              label='Total Amount'
              value={formatAmountInRupees(paymentRequestDetails?.amount)}
            />
          </Layout.Item>
        </StyledLayout>
      </LayoutContainers>

      <TabWrapper>
        <TabContainer>
          <HorizontalTabs
            style={{
              width: 'auto',
              marginBottom: '-4.5px',
              marginLeft: '-3px',
            }}
            alignLinks='left'
            additionalGap='1.8rem'
          >
            <HorizontalTabs.Link
              active={activeIndexGifting === 0}
              onClick={handleTabsClick(0)}
              style={{
                width: '140px',
                color:
                  activeIndexGifting === 0
                    ? 'var(--coolgreycoolgrey-800, #414A5D)'
                    : 'var(--coolgreycoolgrey-400, #AFB4C0)',
                ...tabStyle,
              }}
            >
              Recipients
            </HorizontalTabs.Link>
            <HorizontalTabs.Link
              active={activeIndexGifting === 1}
              onClick={handleTabsClick(1)}
              style={{
                width: '110px',
                color:
                  activeIndexGifting === 1
                    ? 'var(--coolgreycoolgrey-800, #414A5D)'
                    : 'var(--coolgreycoolgrey-400, #AFB4C0)',
                ...tabStyle,
              }}
            >
              Payments
            </HorizontalTabs.Link>
          </HorizontalTabs>
        </TabContainer>
        {showSelectedTable()}
      </TabWrapper>
    </DashboardWrapper>
  );
};

GiftingProgramInfo.propTypes = {
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  programInfo: PropTypes.objectOf(PropTypes.any),
  paymentRequestDetails: PropTypes.objectOf(PropTypes.any),
  getProgramInfo: PropTypes.func,
  fetchingProgramInfo: PropTypes.bool,
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  viewOnlyMode: PropTypes.bool,
};

GiftingProgramInfo.defaultProps = {
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_GIFT_CARD,
  programInfo: {},
  paymentRequestDetails: {},
  getProgramInfo: _noop,
  fetchingProgramInfo: false,
  selectedWorkspace: {},
  viewOnlyMode: false,
};

export default GiftingProgramInfo;
